/**
 * Экскурсия «Что нового» после обновления системы.
 *
 * Как добавить новую выкладку: сменить WHATS_NEW_RELEASE и переписать шаги в WHATS_NEW.
 * Новый ID заставит показать экскурсию всем один раз (флаг хранится в localStorage браузера).
 * Метки в заголовках: «Добавлено», «Изменено», «Убрано».
 */
import type { DriveStep } from 'driver.js'

export const WHATS_NEW_RELEASE = 'r2026-10-30'

type Group = 'director' | 'gip' | 'designer'

const INTRO: DriveStep = {
  popover: {
    title: '🎉 Система обновлена',
    description:
      'Коротко о том, что изменилось. Это займёт минуту: подсвеченные места показывают, где искать новое. ' +
      'Повторить можно в любой момент кнопкой «✨» слева внизу.',
  },
}

const CODES_STEP: DriveStep = {
  element: '[data-tour="nav-projects"]',
  popover: {
    title: '🗂 Изменено: шифры разделов',
    description:
      'Справочник шифров обновлён по ПП-87. У проектной документации появились номера и расшифровки ' +
      '(например, №5.1 ИОС1: Система электроснабжения). В ИРД добавлена группа «Инженерные изыскания» ' +
      '(ОСК, ИГДИ, ИГМИ, ИЭИ, ИГИ). В РД добавлено 15 новых шифров. ' +
      'Устаревшие шифры скрыты из списка выбора, а уже созданные разделы остаются как были.',
    side: 'right',
  },
}

const HISTORY_REMOVED_STEP: DriveStep = {
  popover: {
    title: '➖ Убрано: вкладка «История»',
    description:
      'Вкладка «История» в карточке проекта теперь видна только директору и заместителю: ' +
      'в записях бывают суммы. Всё остальное, что вы делаете в проектах, работает как раньше.',
  },
}

export const WHATS_NEW: Record<Group, DriveStep[]> = {
  director: [
    INTRO,
    CODES_STEP,
    {
      popover: {
        title: '✋ Изменено: статусы разделов',
        description:
          'Директор и заместитель теперь только просматривают статусы разделов. ' +
          'Раздел принимает и возвращает на доработку ГИП проекта. Сроки, исполнителей и состав разделов вы правите по-прежнему.',
      },
    },
    {
      element: '[data-tour="gantt-chart"]',
      popover: {
        title: '📅 Изменено: главный экран ГИП',
        description:
          'ГИП теперь видит все проекты и просрочки всех ГИП. Свои проекты у него идут первыми и яркие, ' +
          'чужие ниже и бледнее; открыть чужой проект он может только для просмотра. Деньги ГИП по-прежнему не видит.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="nav-users"]',
      popover: {
        title: '👥 Добавлено: ГИП приглашает проектировщиков',
        description:
          'Главный инженер сам приглашает проектировщиков (страница «Сотрудники» в его меню). ' +
          'Приглашать ГИП, директора и замов, а также блокировать и удалять сотрудников может только директор и зам.',
        side: 'right',
      },
    },
    {
      popover: {
        title: '✅ Изменено: ГИП может быть исполнителем',
        description:
          'ГИП можно назначать исполнителем раздела и в своём, и в чужом проекте. ' +
          'Свой раздел он ведёт сам, а в своём проекте принимает все разделы, включая собственный.',
      },
    },
    {
      popover: {
        title: '🔒 Без изменений для вас',
        description: 'Вкладка «История» осталась у вас. У ГИП и проектировщиков она убрана, чтобы они не видели суммы.',
      },
    },
  ],

  gip: [
    INTRO,
    {
      element: '[data-tour="gantt-chart"]',
      popover: {
        title: '📅 Изменено: главный экран',
        description:
          'Теперь на Ганте все проекты всех ГИП. Ваши проекты идут первыми и яркие, чужие ниже и бледнее. ' +
          'Чужой проект можно открыть и посмотреть, но не править.',
        side: 'bottom',
      },
    },
    {
      element: '[data-tour="attention-block"]',
      popover: {
        title: '⚠️ Изменено: «Требуют внимания»',
        description:
          'Здесь просрочки и горящие сроки всех ГИП. Ваши разделы стоят первыми, чужие ниже и бледнее.',
        side: 'over',
        align: 'center',
      },
    },
    {
      element: '[data-tour="nav-staff"]',
      popover: {
        title: '➕ Добавлено: «Сотрудники»',
        description:
          'Приглашайте проектировщиков сами: введите e-mail, человек получит ссылку для входа. ' +
          'Здесь же список проектировщиков. Приглашать других ГИП и директоров нельзя.',
        side: 'right',
      },
    },
    {
      popover: {
        title: '✅ Добавлено: вы можете быть исполнителем',
        description:
          'Вас можно назначить исполнителем раздела и в своём, и в чужом проекте. ' +
          'Свой раздел вы ведёте сами (меняете статус). В своём проекте вы принимаете все разделы, включая собственный. ' +
          'В чужом проекте раздел принимает его ГИП. Директор и зам статусы больше не меняют.',
      },
    },
    CODES_STEP,
    {
      popover: {
        title: '➖ Убрано: вкладка «История»',
        description:
          'Вкладка «История» в карточке проекта убрана: в записях бывают суммы, а полный бюджет ГИП видеть не должен. ' +
          'Директор и заместитель видят её по-прежнему.',
      },
    },
  ],

  designer: [
    INTRO,
    CODES_STEP,
    HISTORY_REMOVED_STEP,
    {
      popover: {
        title: '👍 Остальное без изменений',
        description:
          'Ваши разделы, сроки, статусы и процент готовности работают как раньше.',
      },
    },
  ],
}

export function whatsNewGroup(role: string): Group {
  if (role === 'director' || role === 'deputy_director') return 'director'
  if (role === 'chief_engineer') return 'gip'
  return 'designer'
}

export function whatsNewSeenKey(userId: string): string {
  return `whatsnew_${WHATS_NEW_RELEASE}_${userId}`
}
